import {
  AMCReportTypeSelect,
  useGetReportTypesQuery,
} from "~/store/mystore/amc.redux";
import { Box, Grid, Typography } from "@material-ui/core";
import React, { memo, useState } from "react";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";

import AMCReportTypeCard from "~/modules/amc/amcReportTypeCard";
import LoadingIndicator from "~/components/loadingIndicator/loadingIndicator";
import PageBlock from "~/components/containers/sideNavPageBlock";
import Panel from "~/components/panel/panel";
import SearchFilter from "~/components/adTable/searchFilter";
import { get } from "lodash";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useTypedSelector } from "~/hooks/useTypedSelector";

const LibraryWrapper = styled(Grid)`
  max-width: 100%;
  margin: 0 auto;
`;

const LoadingIndicatorWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
`;

const QueryLibrary = memo(function QueryLibrary() {
  const { t } = useTranslation();
  const store = useTypedSelector((state) =>
    get(state, "persistentAppSettings.setting.data.currentStore")
  );

  const [searchText, setSearchText] = useState<string | undefined>();
  const [reportTypeSelect, setReportTypeSelect] = useState(
    AMCReportTypeSelect.ALL
  );

  const { amcReportTypes, reportTypesLoading } = useGetReportTypesQuery(
    {
      searchText,
      filterType: reportTypeSelect,
      activeOnly: true,
    },
    {
      selectFromResult: ({ data, isFetching }) => {
        return {
          amcReportTypes: data?.reportTypes ?? [],
          reportTypesLoading: isFetching,
        };
      },
    }
  );

  const actions = (
    <>
      <Box mr={2}>
        <SearchFilter {...{ setSearchText, autoFocus: false }} />
      </Box>
      <Box>
        <ToggleButtonGroup
          exclusive
          onChange={(_, v) => {
            if (v !== null) setReportTypeSelect(v);
          }}
          value={reportTypeSelect}
          aria-label="report-type-select"
        >
          <ToggleButton value={AMCReportTypeSelect.ALL}>
            <Typography variant="caption">
              {t("amc.reportLibrary.allReportTypeSelect")}
            </Typography>
          </ToggleButton>
          <ToggleButton value={AMCReportTypeSelect.STANDARD}>
            <Typography variant="caption">
              {t("amc.reportLibrary.standardReportTypeSelect")}
            </Typography>
          </ToggleButton>
          <ToggleButton value={AMCReportTypeSelect.CUSTOM}>
            <Typography variant="caption">
              {t("amc.reportLibrary.customReportTypeSelect")}
            </Typography>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </>
  );

  return (
    <PageBlock>
      <Panel
        id="amc-query-library"
        title={t("nav.queryLibrary")}
        actions={actions}
        content={
          <>
            {!reportTypesLoading || !store ? (
              amcReportTypes.length > 0 ? (
                <LibraryWrapper container spacing={2}>
                  {amcReportTypes.map((reportType) => (
                    <Grid item xs={12} sm={6} md={4} key={reportType._id}>
                      <AMCReportTypeCard
                        mid={store?.merchantId || ""}
                        reportType={reportType}
                      />
                    </Grid>
                  ))}
                </LibraryWrapper>
              ) : (
                <Box p={2}>
                  <Typography variant="body1">
                    {t("amc.reportLibrary.noReportsMsg")}
                  </Typography>
                </Box>
              )
            ) : (
              <LoadingIndicatorWrapper>
                <LoadingIndicator />
              </LoadingIndicatorWrapper>
            )}
          </>
        }
      />
    </PageBlock>
  );
});

export default QueryLibrary;
